import React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { FONTSIZE, CSS_PRIMARY, PRIMARY_COLOR_HOVER } from '../../../../constants/style';

const DatePicker = ({
  mode = 'single',
  value = mode === 'range' ? { startDate: null, endDate: null } : null,
  onChange = () => { },
  label,
  placeholder = 'Select date',
  minDate,
  maxDate,
  color = CSS_PRIMARY,
  dateFormat = 'dd/MM/yyyy',
  className = '',
  width = '100%',
  height = 36,
  onKeyDown = () => { },
  readOnly = false,
  disabled = false
}) => {
  const isRange = mode === 'range';

  // Format string needs to be uppercase for dayjs (e.g., DD/MM/YYYY)
  const formatStr = dateFormat.toUpperCase();

  const datePickerSx = {
    width: isRange ? '100%' : width,
    '& .MuiPickersInputBase-root': {
      height: `${height}px !important`,
      minHeight: `${height}px !important`
    },
    '& .MuiOutlinedInput-root': {
      height: `${height}px !important`
    },
    '& input': {
      padding: '6px 8px !important',
      fontSize: FONTSIZE
    }
  };

  if (isRange) {
    // Determine the start and end values.
    const startVal = value?.startDate;
    const endVal = value?.endDate;

    // MUI Picker expects dayjs objects or null
    const startDate = startVal ? dayjs(startVal) : null;
    const endDate = endVal ? dayjs(endVal) : null;

    const handleChange = (newValue) => {
      const [newStart, newEnd] = newValue;
      const sd = newStart && newStart.isValid() ? newStart.startOf('day').toDate() : null;
      const ed = newEnd && newEnd.isValid() ? newEnd.startOf('day').toDate() : null;
      onChange({ startDate: sd, endDate: ed });
    };

    const placeholders = placeholder.split(' - ');
    const startPlaceholder = placeholders[0] || 'Start Date';
    const endPlaceholder = placeholders.length > 1 ? placeholders[1] : 'End Date';

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ width: width }} className={className}>
          <Box display="flex" width="100%">
            <DateRangePicker
              format={formatStr}
              value={[startDate, endDate]}
              onChange={handleChange}
              minDate={minDate ? dayjs(minDate) : undefined}
              maxDate={maxDate ? dayjs(maxDate) : undefined}
              localeText={{ start: startPlaceholder, end: endPlaceholder }}
              readOnly={readOnly}
              disabled={disabled}
              label={label}
              slotProps={{
                field: {
                  sx: datePickerSx
                },
                textField: {
                  size: 'small',
                  onKeyDown: onKeyDown
                }
              }}
            />
          </Box>
        </Box>
      </LocalizationProvider>
    );
  }

  // Single mode
  const singleDate = value ? dayjs(value) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: width }} className={className}>
        <MUIDatePicker
          format={formatStr}
          value={singleDate}
          label={label}
          onChange={(newDate) => {
            onChange(newDate && newDate.isValid() ? newDate.toDate() : null);
          }}
          minDate={minDate ? dayjs(minDate) : undefined}
          maxDate={maxDate ? dayjs(maxDate) : undefined}
          readOnly={readOnly}
          disabled={disabled}
          placeholder={placeholder}
          slotProps={{
            field: {
              sx: datePickerSx
            },
            textField: {
              size: 'small',
              onKeyDown: onKeyDown,
              sx: {
                width: width,
                '& .MuiOutlinedInput-root': {
                  color: CSS_PRIMARY,

                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: CSS_PRIMARY
                  },

                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: PRIMARY_COLOR_HOVER
                  },

                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: CSS_PRIMARY,
                    borderWidth: '1px'
                  }
                },

                '& .MuiInputLabel-root': {
                  color: CSS_PRIMARY,
                  fontSize: '12px'
                },

                '& .MuiInputLabel-root.Mui-focused': {
                  color: CSS_PRIMARY
                },

                '& .MuiInputBase-input': {
                  fontSize: FONTSIZE
                },

                '& .MuiSvgIcon-root': {
                  color: CSS_PRIMARY
                }
              }
            }
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default DatePicker;
